import { Directive, ElementRef, inject, input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
  selector: '[appTooltip]',
})
export class UiTooltip implements OnDestroy {
  readonly appTooltip = input<string | null>(null);
  readonly tooltipPosition = input<'top' | 'bottom' | 'left' | 'right'>('top');

  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private tooltipElement: HTMLElement | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private unlisteners: (() => void)[] = [];

  constructor() {
    this.unlisteners.push(
      this.renderer.listen(this.el.nativeElement, 'mouseenter', () => this.show()),
      this.renderer.listen(this.el.nativeElement, 'mouseleave', () => this.hide()),
      this.renderer.listen(this.el.nativeElement, 'focus', () => this.show()),
      this.renderer.listen(this.el.nativeElement, 'blur', () => this.hide()),
    );
  }

  private show(): void {
    const text = this.appTooltip();
    if (!text) return;

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    if (this.tooltipElement) {
      this.removeTooltipElement();
    }

    this.tooltipElement = this.renderer.createElement('div');
    const tooltipText = this.renderer.createText(text);
    this.renderer.appendChild(this.tooltipElement, tooltipText);

    const baseClasses =
      'fixed z-[9999] px-2.5 py-1.5 text-xs font-medium text-white bg-slate-900 rounded-lg shadow-lg whitespace-nowrap pointer-events-none transition-opacity duration-150 opacity-0';
    this.renderer.setAttribute(this.tooltipElement, 'class', baseClasses);
    this.renderer.setAttribute(this.tooltipElement, 'role', 'tooltip');
    this.renderer.appendChild(document.body, this.tooltipElement);

    const rect = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement!.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (this.tooltipPosition()) {
      case 'top':
        top = rect.top - tooltipRect.height - 8;
        left = rect.left + (rect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = rect.bottom + 8;
        left = rect.left + (rect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = rect.top + (rect.height - tooltipRect.height) / 2;
        left = rect.left - tooltipRect.width - 8;
        break;
      case 'right':
        top = rect.top + (rect.height - tooltipRect.height) / 2;
        left = rect.right + 8;
        break;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${Math.max(4, top)}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${Math.max(4, left)}px`);
    this.renderer.setStyle(this.tooltipElement, 'opacity', '1');
  }

  private hide(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.tooltipElement) {
      this.renderer.setStyle(this.tooltipElement, 'opacity', '0');
      this.hideTimer = setTimeout(() => {
        this.removeTooltipElement();
        this.hideTimer = null;
      }, 200);
    }
  }

  private removeTooltipElement(): void {
    if (this.tooltipElement) {
      if (this.tooltipElement.parentNode) {
        this.renderer.removeChild(document.body, this.tooltipElement);
      }
      this.tooltipElement = null;
    }
  }

  ngOnDestroy(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.removeTooltipElement();
    this.unlisteners.forEach((fn) => fn());
    this.unlisteners = [];
  }
}
